import type {
  Placement,
  ProStudioNodeDataStore,
} from '@pypestream/pro-studio-collab-client';

import type { TableRow } from '../stores/table-store';
import {
  ARRAY_VALUED_NODE_COLUMNS,
  collectRowArrayColumnFixes,
  type ConvertCellText,
} from '../stores/utils/coerce-row-array-columns';
import { toNewNode, toNodePatch } from './table-rows-to-collab';

/**
 * The table's save funnels, pointed at the collab store (PSTUDIO-4451).
 *
 * The table store keeps doing what it does — mutate its rows, then save full
 * rows, orders or deletes — and under live editing those three funnels land
 * here instead of Hasura. Each call is one `transact`, so one gesture is one
 * Yjs transaction and one undo step. `sortedRows` is the table's current
 * order, the only source of where a new or moved row belongs: the document
 * has no `order` the table may write.
 */
export type CollabWriter = {
  saveRows(rows: readonly Partial<TableRow>[]): void;
  moveRows(ids: readonly string[]): void;
  deleteRows(ids: readonly string[]): void;
};

type Store = Pick<
  ProStudioNodeDataStore,
  | 'nodes'
  | 'transact'
  | 'updateNodes'
  | 'createNodes'
  | 'moveNodes'
  | 'deleteNodes'
>;

/**
 * PSTUDIO-4449: a row can reach a save holding a STRING in an array column —
 * the Expanded Cell View keeps the raw typed text there until it commits
 * (PSTUDIO-3882), and any other save of that row in the meantime carries it.
 * The projection turns every non-list into `[]`, which would overwrite the
 * stored list. So, with the Hasura path's converter (`coerceRowArrayColumns`,
 * PSTUDIO-4081): convert what converts. What still is not a list is where the
 * two paths part on purpose: the Hasura upsert kept it and failed the chunk
 * loudly; here it is left out of the update — the stored list stays, the draft
 * stays in the panel for its own commit — and reported (`onUnwritable`),
 * since the user is told nothing.
 */
const withArrayColumnsFixed = (
  row: TableRow,
  convert: ConvertCellText
): {
  row: TableRow;
  unwritable: (typeof ARRAY_VALUED_NODE_COLUMNS)[number][];
} => {
  const fixes = collectRowArrayColumnFixes(row, convert);
  const fixed = (fixes ? { ...row, ...fixes } : row) as TableRow;
  const source = fixed as unknown as Record<string, unknown>;
  const unwritable = ARRAY_VALUED_NODE_COLUMNS.filter((column) => {
    const value = source[column];
    return value !== null && value !== undefined && !Array.isArray(value);
  });
  return { row: fixed, unwritable };
};

export type UnwritableDraft = {
  rowId: string;
  columns: string[];
  /** A new node: the column was created empty — the document had no value. */
  created: boolean;
};

export const createCollabWriter = (
  store: Store,
  sortedRows: () => readonly TableRow[],
  {
    convert,
    onUnwritable = () => undefined,
  }: {
    convert: ConvertCellText;
    /** A draft left out of the write; the user sees nothing, so we report it. */
    onUnwritable?: (draft: UnwritableDraft) => void;
  }
): CollabWriter => {
  /**
   * Between the nearest neighbours that are already in the document. A
   * neighbour created earlier in the same save counts — `alsoPresent`, since
   * the store itself only shows new nodes once the transaction ends.
   */
  const placeAmong = (
    sorted: readonly TableRow[],
    { first, last }: { first: number; last: number },
    alsoPresent: ReadonlySet<string> = new Set()
  ): Placement => {
    const present = (id: string) => store.nodes.has(id) || alsoPresent.has(id);
    for (let i = first - 1; i >= 0; i -= 1) {
      if (present(sorted[i].id)) return { after: sorted[i].id };
    }
    for (let i = last + 1; i < sorted.length; i += 1) {
      if (present(sorted[i].id)) return { before: sorted[i].id };
    }
    return {};
  };

  /**
   * A copied task row carries the original's `settings.taskId`; it is a new
   * node, not a second task. A row is a task when it names itself, or a task
   * id no node of the document already holds (the Supervisor panel creating
   * one).
   */
  const isNewTask = (row: TableRow) => {
    const taskId = row.settings?.taskId;
    if (!taskId) return false;
    if (taskId === row.id) return true;
    return !store.nodes.get(taskId)?.isTask;
  };

  const saveRows: CollabWriter['saveRows'] = (rows) => {
    const sorted = sortedRows();
    const byId = new Map(sorted.map((r) => [r.id, r]));
    const updates: Array<{ id: string; patch: Record<string, unknown> }> = [];
    const created: TableRow[] = [];

    for (const partial of rows) {
      if (!partial.id) continue;
      // A save may carry a partial row; the table's own row fills the rest.
      const { row, unwritable } = withArrayColumnsFixed(
        { ...byId.get(partial.id), ...partial } as TableRow,
        convert
      );
      const current = store.nodes.get(row.id);
      if (current) {
        const patch = toNodePatch(row, current);
        for (const column of unwritable) delete patch[column];
        if (unwritable.length > 0) {
          onUnwritable({ rowId: row.id, columns: unwritable, created: false });
        }
        if (Object.keys(patch).length > 0) updates.push({ id: row.id, patch });
      } else {
        // A new node has no stored value to keep: the column starts empty,
        // and the draft is still in the panel for its own commit.
        if (unwritable.length > 0) {
          onUnwritable({ rowId: row.id, columns: unwritable, created: true });
        }
        created.push(row);
      }
    }
    if (updates.length === 0 && created.length === 0) return;

    // New rows in table order, so each one's earlier neighbour exists first.
    const position = (id: string) => sorted.findIndex((r) => r.id === id);
    created.sort((a, b) => position(a.id) - position(b.id));

    store.transact(() => {
      if (updates.length > 0) store.updateNodes(updates);
      const createdIds = new Set<string>();
      for (const row of created) {
        const at = position(row.id);
        store.createNodes(
          { ...toNewNode(row), isTask: isNewTask(row) },
          at === -1
            ? placeAmong(
                sorted,
                { first: sorted.length, last: sorted.length },
                createdIds
              )
            : placeAmong(sorted, { first: at, last: at }, createdIds)
        );
        createdIds.add(row.id);
      }
    });
  };

  const moveRows: CollabWriter['moveRows'] = (ids) => {
    const sorted = sortedRows();
    const moving = new Set(ids.filter((id) => store.nodes.has(id)));
    if (moving.size === 0) return;

    // Contiguous runs in the table's new order; each run moves as one block.
    const runs: Array<{ ids: string[]; first: number; last: number }> = [];
    sorted.forEach((row, index) => {
      if (!moving.has(row.id)) return;
      const run = runs[runs.length - 1];
      if (run && run.last === index - 1) {
        run.ids.push(row.id);
        run.last = index;
      } else {
        runs.push({ ids: [row.id], first: index, last: index });
      }
    });

    store.transact(() => {
      for (const run of runs) {
        // Neighbours outside the run; a neighbour that is itself moving
        // lands first because runs go in table order.
        store.moveNodes(run.ids, placeAmong(sorted, run));
      }
    });
  };

  const deleteRows: CollabWriter['deleteRows'] = (ids) => {
    const present = ids.filter((id) => store.nodes.has(id));
    if (present.length > 0) store.deleteNodes(present);
  };

  return { saveRows, moveRows, deleteRows };
};
